import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

/* ──────────────────────────────────────────────────────────────────────────
 * Inquiry Cart = a quotation-request basket, NOT an e-commerce checkout.
 * No pricing, no payment, no orders. On submit it packs the full item list +
 * customer details into the `message` field of the EXISTING enquiry endpoint
 * (POST /website/public/enquiry accepts name, phone, email, enquiry_type,
 * message only) so the server and admin panel stay untouched.
 * State persists in localStorage so the basket survives navigation/refresh.
 * ────────────────────────────────────────────────────────────────────────── */

const InquiryCartContext = createContext(null);
const STORAGE_KEY = 'moji_inquiry_cart_v1';

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function InquiryCartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage full / disabled — cart simply won't persist */
    }
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    if (!product?.id) return;
    setItems((prev) => {
      const i = prev.findIndex((x) => String(x.id) === String(product.id));
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          slug: product.slug || '',
          name: product.name,
          category: product.category || '',
          image_url: product.image_url || '',
          qty: Math.max(1, qty),
        },
      ];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev.map((x) => (String(x.id) === String(id) ? { ...x, qty: Math.max(1, qty) } : x))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const isInCart = useCallback(
    (id) => items.some((x) => String(x.id) === String(id)),
    [items]
  );

  const count = useMemo(() => items.reduce((n, x) => n + x.qty, 0), [items]);

  /** Build the human-readable enquiry body and POST it to the existing endpoint. */
  const submitInquiry = useCallback(
    async (customer) => {
      const lines = items.map(
        (it, i) =>
          `${i + 1}. ${it.name}  ×${it.qty}` +
          `\n   Product ID: ${it.id}` +
          (it.category ? `\n   Category: ${it.category}` : '') +
          (it.image_url ? `\n   Image: ${it.image_url}` : '')
      );

      const header = [
        '── INQUIRY CART REQUEST ──',
        customer.company ? `Company: ${customer.company}` : null,
        customer.city || customer.country
          ? `Location: ${[customer.city, customer.country].filter(Boolean).join(', ')}`
          : null,
        customer.preferred_contact ? `Preferred contact: ${customer.preferred_contact}` : null,
        customer.project_type ? `Project type: ${customer.project_type}` : null,
      ]
        .filter(Boolean)
        .join('\n');

      const message = [
        header,
        '',
        `Items (${items.length}):`,
        lines.join('\n'),
        '',
        customer.message ? `Notes:\n${customer.message}` : null,
      ]
        .filter((x) => x !== null && x !== undefined)
        .join('\n');

      const payload = {
        name: customer.name,
        phone: customer.phone,
        email: customer.email || '',
        enquiry_type: customer.project_type || 'Inquiry Cart',
        message,
      };

      await api.post('/website/public/enquiry', payload);
      clear();
    },
    [items, clear]
  );

  const value = useMemo(
    () => ({
      items,
      count,
      open,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      addItem,
      removeItem,
      setQty,
      clear,
      isInCart,
      submitInquiry,
    }),
    [items, count, open, addItem, removeItem, setQty, clear, isInCart, submitInquiry]
  );

  return <InquiryCartContext.Provider value={value}>{children}</InquiryCartContext.Provider>;
}

export function useInquiryCart() {
  const ctx = useContext(InquiryCartContext);
  if (!ctx) throw new Error('useInquiryCart must be used within InquiryCartProvider');
  return ctx;
}
